import { createSlice } from "@reduxjs/toolkit";

export const INITIAL_FLIGHT_FORM = {
  origin: "",
  destination: "",
  departureDate: "",
  returnDate: "",
  passengers: 1,
  currency: "GEL",
};

export const INITIAL_HOTEL_FORM = {
  city: "",
  checkIn: "",
  checkOut: "",
  guests: 1,
};

const INITIAL_TAB_STATE = {
  results: [],
  hasSearched: false,
  searchTimestamp: null,
  currencyFallback: false,
  scraperSearchId: null,
  flightBuySearchId: null,
  providerStatuses: {},
  quota: null,
};

function createInitialTabs() {
  return {
    flights: {
      ...INITIAL_TAB_STATE,
      formData: { ...INITIAL_FLIGHT_FORM },
    },
    hotels: {
      ...INITIAL_TAB_STATE,
      formData: { ...INITIAL_HOTEL_FORM },
    },
  };
}

const travelSearchSlice = createSlice({
  name: "travelSearch",
  initialState: {
    searchType: "flights",
    formExpanded: true,
    tabs: createInitialTabs(),
  },
  reducers: {
    restoreTravelSearchState: (state, action) => {
      const { searchType, forms, sessions } = action.payload;
      state.searchType = searchType;
      for (const tab of ["flights", "hotels"]) {
        const previous = state.tabs[tab];
        const sessionId = sessions[tab] || null;
        const sameForm = JSON.stringify(previous.formData) === JSON.stringify(forms[tab]);
        if (previous.scraperSearchId !== sessionId || !sameForm) {
          state.tabs[tab] = { ...INITIAL_TAB_STATE, formData: forms[tab], scraperSearchId: sessionId, hasSearched: !!sessionId };
        } else {
          previous.formData = forms[tab];
        }
      }
    },
    setTravelSearchFormData: (state, action) => {
      const tab = state.searchType;
      state.tabs[tab].formData = { ...state.tabs[tab].formData, ...action.payload };
    },
    setTravelSearchType: (state, action) => {
      state.searchType = action.payload;
    },
    setTravelSearchFormExpanded: (state, action) => {
      state.formExpanded = action.payload;
    },
    clearTravelSearch: (state) => {
      const tab = state.searchType;
      const initialForm =
        tab === "hotels" ? { ...INITIAL_HOTEL_FORM } : { ...INITIAL_FLIGHT_FORM };
      state.tabs[tab] = { ...INITIAL_TAB_STATE, formData: initialForm };
    },
    setTravelSearchResults: (state, action) => {
      const tab = action.payload.tab || state.searchType;
      state.tabs[tab].results =
        action.payload.results !== undefined ? action.payload.results : state.tabs[tab].results;
      state.tabs[tab].hasSearched =
        action.payload.hasSearched !== undefined ? action.payload.hasSearched : state.tabs[tab].hasSearched;
      state.tabs[tab].searchTimestamp =
        action.payload.searchTimestamp !== undefined
          ? action.payload.searchTimestamp
          : state.tabs[tab].searchTimestamp;
      state.tabs[tab].currencyFallback = action.payload.currencyFallback ?? false;
      if (action.payload.resetScraper) {
        state.tabs[tab].scraperSearchId = null;
        state.tabs[tab].flightBuySearchId = null;
        state.tabs[tab].providerStatuses = {};
      }
    },
    initScraperSession: (state, action) => {
      const tab = action.payload.tab || state.searchType;
      const { searchId, searchTimestamp, providerStatuses } = action.payload;
      state.tabs[tab].scraperSearchId = searchId;
      state.tabs[tab].flightBuySearchId = null;
      state.tabs[tab].results = [];
      state.tabs[tab].providerStatuses = providerStatuses || {};
      state.tabs[tab].hasSearched = true;
      state.tabs[tab].searchTimestamp = searchTimestamp ?? new Date().toISOString();
      state.tabs[tab].currencyFallback = false;
      state.tabs[tab].quota = action.payload.quota || null;
    },
    syncScraperSnapshot: (state, action) => {
      const snap = action.payload;
      if (!snap?.searchId) return;

      let targetTab = null;
      for (const t of Object.keys(state.tabs)) {
        if (state.tabs[t].scraperSearchId === snap.searchId) {
          targetTab = t;
          break;
        }
      }

      if (!targetTab) return;

      const raw = snap.offers || snap.results || [];
      const sortPrice = (item) => Number(item.price) > 0 ? Number(item.price) : Infinity;
      const next = [...raw].sort((a, b) => sortPrice(a) - sortPrice(b));
      state.tabs[targetTab].results = next;
      state.tabs[targetTab].providerStatuses = { ...(snap.providerStatuses || {}) };
      if (snap.quota) state.tabs[targetTab].quota = snap.quota;
      if (snap.flightSearchId) {
        state.tabs[targetTab].flightBuySearchId = snap.flightSearchId;
      }
      if (snap.scraperCurrencyFallback != null) {
        state.tabs[targetTab].currencyFallback = !!snap.scraperCurrencyFallback;
      }
    },
  },
});

export const {
  restoreTravelSearchState,
  setTravelSearchFormData,
  setTravelSearchType,
  setTravelSearchFormExpanded,
  clearTravelSearch,
  setTravelSearchResults,
  initScraperSession,
  syncScraperSnapshot,
} = travelSearchSlice.actions;

export default travelSearchSlice.reducer;
